import { useUpdateAtom } from "jotai/utils";
import React from "react";
import { ToastVariant } from "../components/Toaster/Toast";
import { ToastInvocation, toastsAtom } from "../components/Toaster/toaster.atom";

export const TOAST_ENTER_ANIMATION_DURATION = 500;
export const TOAST_DISMISS_ANIMATION_DURATION = 250;

interface CreateToastParams {
  key: string;
  message: React.ReactNode;
  variant?: ToastVariant;
  isLoading?: boolean;
  timeout?: number;
  closeable?: boolean;
}

interface UpdateToastParams {
  message?: React.ReactNode;
  variant?: ToastVariant;
  isLoading?: boolean;
  timeout?: number;
}

const useToast = () => {
  const setToasts = useUpdateAtom(toastsAtom);
  const timeouts = React.useRef<{ [key: string]: NodeJS.Timeout }>({});

  /**
   * Create a new toast
   * @param invocation the toast invocation payload
   */
  const create = (toast: CreateToastParams) => {
    resetTimeoutIfExists(toast.key);

    setToasts((prevToasts) => {
      if (prevToasts.find((x) => x.key === toast.key) !== undefined) {
        console.warn(
          [
            `Tried to create a new toast with the same key (${toast.key}).`,
            "If you meant to update the existing toast, use `update` instead.",
          ].join("\n")
        );
      }

      return [
        ...prevToasts.filter((toaster) => toast.key !== toaster.key),
        mapCreatedToastToInvocation(toast),
      ];
    });

    if (toast.timeout !== undefined) {
      timeouts.current[toast.key] = setTimeout(() => dismiss(toast.key), toast.timeout);
    }

    return toast.key;
  };

  /**
   * Update an existing invocation toast and resets the dismissal timeout
   * @param key The key of the invocation payload to be updated
   * @param toast The new payload that would be merged to the existing invocation payload
   */
  const update = (key: string, toast: UpdateToastParams) => {
    resetTimeoutIfExists(key);

    setToasts((prevToasts) => {
      return prevToasts.map((prevToast) => {
        return prevToast.key === key
          ? mapUpdatedToastToInvocation(prevToasts, key, toast)
          : prevToast;
      });
    });

    if (toast.timeout !== undefined) {
      timeouts.current[key] = setTimeout(() => dismiss(key), toast.timeout);
    }
  };

  /**
   * Dismiss an existing toast
   * @param key They key of the toast invocation that should be dismissed
   */
  const dismiss = (key: string) => {
    setAsDismissing(key);

    timeouts.current[key] = setTimeout(
      () => removeFromState(key),
      TOAST_DISMISS_ANIMATION_DURATION
    );
  };

  const mapCreatedToastToInvocation = (toast: CreateToastParams): ToastInvocation => {
    return {
      key: toast.key,
      toast: {
        isLoading: toast.isLoading ?? false,
        message: toast.message,
        variant: toast.variant ?? "neutral",
        onRequestClose: toast.closeable === false ? undefined : () => dismiss(toast.key),
      },
      timeout: toast.timeout ?? null,
      isDismissing: false,
    };
  };

  const mapUpdatedToastToInvocation = (
    toasts: ToastInvocation[],
    key: string,
    toast: UpdateToastParams
  ): ToastInvocation => {
    const prevToast = toasts.find((toaster) => toaster.key === key);

    if (prevToast === undefined) {
      throw new Error(`Can't update toast "${key}" after the toast has already been removed`);
    }

    return {
      key: key,
      toast: {
        isLoading: toast.isLoading ?? prevToast.toast.isLoading,
        message: toast.message ?? prevToast.toast.message,
        variant: toast.variant ?? prevToast.toast.variant,
        onRequestClose: prevToast.toast.onRequestClose,
      },
      timeout: toast.timeout ?? prevToast.timeout ?? null,
      isDismissing: false,
    };
  };

  /**
   * Reset a dismiss/close timeout of a toast if exists
   * @param key the key of the toast invocation
   */
  const resetTimeoutIfExists = (key: string) => {
    if (key in timeouts.current) {
      clearTimeout(timeouts.current[key]);
    }
  };

  /**
   * Remove a toast invocation from the toast state
   * @param key The key of the toast invocation that should be removed
   */
  const removeFromState = (key: string) => {
    setToasts((prevToasts) => prevToasts.filter((toast) => toast.key !== key));
  };

  /**
   * Set an existing toast as dismissing
   * @param key The key of the toast invocation that should be set as dismissing
   */
  const setAsDismissing = (key: string) => {
    setToasts((prevToasts) => {
      return prevToasts.map((toast) => {
        return toast.key === key ? { ...toast, isDismissing: true } : toast;
      });
    });
  };

  return { create, update, dismiss };
};

export default useToast;
